import React, { useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Box,
  Button,
  Center,
  Group,
  Image,
  Loader,
  Menu,
  Paper,
  Text,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronUp,
  IconPlayerPlay,
  IconPlayerStop,
  IconRefresh,
  IconResize,
  IconZoomIn,
  IconZoomOut,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { SCOPE_TYPE } from "@model";
import { useAuthContext } from "@service";
import { reconnectWebRTC, setSceneViewEnabled } from "@utils/api-communication/WebSocketCli";
import { isVersionEnoughForHeadsetSceneView } from "@utils/VersionUtils";
import { selectCurrentVersion } from "@utils/redux-utils/selectors/productSelectors";
import { selectIsWebRTCConnected } from "@store/connection/connectionSelectors";
import { clearSceneImage, subscribeSceneImage } from "@utils/api-communication/WebRTCUtil";

const STALE_FRAME_TIMEOUT_MS = 5000;

const SCENE_VIEW_SIZE_KEY = "headsetSceneViewSize";

type SceneViewSize = "small" | "medium" | "large";

const SCENE_VIEW_WIDTHS: Record<SceneViewSize, number> = {
  small: 256,
  medium: 420,
  large: 640,
};

const SCENE_VIEW_SIZES = Object.keys(SCENE_VIEW_WIDTHS) as SceneViewSize[];

const DEFAULT_SCENE_VIEW_SIZE: SceneViewSize = "medium";

const SCENE_VIEW_ASPECT_RATIO = 9 / 16;
const SCENE_VIEW_ZOOM_KEY = "headsetSceneViewZoom";
const MIN_SCENE_VIEW_ZOOM = 1;
const MAX_SCENE_VIEW_ZOOM = 3;
const SCENE_VIEW_ZOOM_STEP = 0.25;
const DEFAULT_SCENE_VIEW_ZOOM = 1.5;

function clampZoom(value: number): number {
  return Math.min(MAX_SCENE_VIEW_ZOOM, Math.max(MIN_SCENE_VIEW_ZOOM, value));
}

function readSceneViewPreference(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.error(`Failed to read ${key} from storage:`, error);
    return null;
  }
}

function storeSceneViewPreference(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    console.error(`Failed to store ${key}:`, error);
  }
}

function readStoredSceneViewZoom(): number {
  const stored = Number(readSceneViewPreference(SCENE_VIEW_ZOOM_KEY));
  return Number.isFinite(stored) && stored > 0 ? clampZoom(stored) : DEFAULT_SCENE_VIEW_ZOOM;
}

function storeSceneViewZoom(zoom: number): void {
  storeSceneViewPreference(SCENE_VIEW_ZOOM_KEY, String(zoom));
}

function readStoredSceneViewSize(): SceneViewSize {
  const stored = readSceneViewPreference(SCENE_VIEW_SIZE_KEY) as SceneViewSize | null;
  return stored && stored in SCENE_VIEW_WIDTHS ? stored : DEFAULT_SCENE_VIEW_SIZE;
}

function storeSceneViewSize(size: SceneViewSize): void {
  storeSceneViewPreference(SCENE_VIEW_SIZE_KEY, size);
}

const HeadsetSceneView: React.FC = () => {
  const { t } = useTranslation(undefined, { useSuspense: false });
  const currentVersion = useSelector(selectCurrentVersion);
  const isWebRTCConnected = useSelector(selectIsWebRTCConnected);
  const { scopes } = useAuthContext();
  const isSupported =
    scopes.includes(SCOPE_TYPE.screenStreaming) &&
    isVersionEnoughForHeadsetSceneView(currentVersion);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [size, setSize] = useState<SceneViewSize>(readStoredSceneViewSize);
  const [zoom, setZoom] = useState<number>(readStoredSceneViewZoom);
  const staleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isSupported || !playing) return undefined;

    setSceneViewEnabled(true);
    const unsubscribe = subscribeSceneImage((url) => {
      if (staleTimer.current) {
        clearTimeout(staleTimer.current);
        staleTimer.current = null;
      }
      if (!url) {
        setImageUrl(null);
        return;
      }
      setImageUrl(url);
      staleTimer.current = setTimeout(() => setImageUrl(null), STALE_FRAME_TIMEOUT_MS);
    });
    return () => {
      unsubscribe();
      setSceneViewEnabled(false);
      clearSceneImage();
      if (staleTimer.current) {
        clearTimeout(staleTimer.current);
        staleTimer.current = null;
      }
      setImageUrl(null);
    };
  }, [isSupported, playing]);

  const handleCollapse = () => {
    setOpen(false);
    setPlaying(false);
  };

  const handleExpand = () => {
    setOpen(true);
    setPlaying(true);
  };

  const handlePlayToggle = () => {
    setPlaying((current) => !current);
  };

  const handleSizeChange = (nextSize: SceneViewSize) => {
    setSize(nextSize);
    storeSceneViewSize(nextSize);
  };

  const handleZoomChange = (delta: number) => {
    setZoom((current) => {
      const next = clampZoom(current + delta);
      storeSceneViewZoom(next);
      return next;
    });
  };

  const handleReconnect = () => {
    setImageUrl(null);
    reconnectWebRTC();
  };

  if (!isSupported) return null;

  if (!open) {
    if (!isWebRTCConnected) return null;
    return (
      <Button
        variant="default"
        size="compact-sm"
        radius="xl"
        leftSection={<IconChevronUp size={14} />}
        onClick={handleExpand}
        style={{ position: "fixed", left: 16, bottom: 16, zIndex: 1100 }}
      >
        {t("headsetView")}
      </Button>
    );
  }

  return (
    <Paper
      withBorder
      shadow="md"
      radius="md"
      p="xs"
      style={{
        position: "fixed",
        left: 16,
        bottom: 16,
        zIndex: 1100,
        width: `min(${SCENE_VIEW_WIDTHS[size]}px, calc(100vw - 32px))`,
        maxWidth: "calc(100vw - 32px)",
        overflow: "hidden",
      }}
    >
      <Group justify="space-between" gap={4} mb={4} wrap="nowrap">
        <Text fw={600} size="xs">
          {t("headsetView")}
        </Text>
        <Group gap={2} wrap="nowrap">
          <ActionIcon
            variant="subtle"
            color={playing ? "red" : "green"}
            size="sm"
            aria-label={playing ? t("headsetViewStop") : t("headsetViewPlay")}
            onClick={handlePlayToggle}
          >
            {playing ? <IconPlayerStop size={14} /> : <IconPlayerPlay size={14} />}
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            aria-label={t("headsetViewZoomOut")}
            disabled={zoom <= MIN_SCENE_VIEW_ZOOM}
            onClick={() => handleZoomChange(-SCENE_VIEW_ZOOM_STEP)}
          >
            <IconZoomOut size={14} />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            aria-label={t("headsetViewZoomIn")}
            disabled={zoom >= MAX_SCENE_VIEW_ZOOM}
            onClick={() => handleZoomChange(SCENE_VIEW_ZOOM_STEP)}
          >
            <IconZoomIn size={14} />
          </ActionIcon>
          <Menu position="top-end" withinPortal>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray" size="sm" aria-label={t("headsetViewSize")}>
                <IconResize size={14} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>{t("headsetViewSize")}</Menu.Label>
              {SCENE_VIEW_SIZES.map((option) => (
                <Menu.Item
                  key={option}
                  onClick={() => handleSizeChange(option)}
                  fw={option === size ? 700 : undefined}
                >
                  {t(`headsetViewSize${option.charAt(0).toUpperCase()}${option.slice(1)}`)}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            aria-label={t("reconnect")}
            onClick={handleReconnect}
          >
            <IconRefresh size={14} />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            aria-label={t("minimize")}
            onClick={handleCollapse}
          >
            <IconChevronDown size={14} />
          </ActionIcon>
        </Group>
      </Group>
      <Box
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: `${1 / SCENE_VIEW_ASPECT_RATIO}`,
          overflow: "hidden",
          borderRadius: "var(--mantine-radius-sm)",
        }}
      >
        {!isWebRTCConnected ? (
          <Center h="100%">
            <Text size="xs" c="dimmed">
              {t("headsetViewDisconnected")}
            </Text>
          </Center>
        ) : !playing ? (
          <Center h="100%">
            <Text size="xs" c="dimmed">
              {t("headsetViewStopped")}
            </Text>
          </Center>
        ) : imageUrl ? (
          <Image
            src={imageUrl}
            alt={t("headsetView")}
            fit="cover"
            h="100%"
            w="100%"
            style={{ transform: `scale(${zoom})`, transformOrigin: "center" }}
          />
        ) : (
          <Center h="100%">
            <Loader size="sm" />
          </Center>
        )}
      </Box>
    </Paper>
  );
};

export default HeadsetSceneView;
